import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '@dbt-labs/sourdough';
import { type Ryecon } from '@dbt-labs/sourdough';

import { InteractionDagNode } from '../dag/internalDag';
import { DbtDagNode, getLensInfo, LensDisplayInfo, useDagContext } from '../dbt-dag';
import { ResourceTypeExplorer } from './ResourceTypeIcon';

interface LensNodeBadgeDisplayProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'data'> {
  isMagnified: boolean;
  disableMagnifiedNode: boolean;
  icon?: Ryecon;
  color?: string;
  label?: string;
}

const LensNodeBadgeDisplay = ({
  className,
  isMagnified,
  disableMagnifiedNode,
  icon,
  color,
  label,
  ...divProps
}: LensNodeBadgeDisplayProps) => {
  return (
    <div
      {...divProps}
      className={twMerge(
        `box-content rounded-md border px-2 py-1.5`,
        !isMagnified &&
          !disableMagnifiedNode &&
          'zoom-level-quarter:hidden zoom-level-half:hidden',
        className,
        color,
      )}
    >
      <span data-testid="badge-text-container">
        {icon && (
          <span className="align-center flex items-center">
            <Icon className="flex-1 pr-1" ryecon={icon} testId="badge-icon" />
            {label?.toUpperCase()}
          </span>
        )}
        {!icon && label?.toUpperCase()}
      </span>
    </div>
  );
};

interface LensNodeBadgeProps<TDbtDagNode extends DbtDagNode>
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'data'> {
  data: InteractionDagNode<TDbtDagNode>;
  isMagnified: boolean;
  resourceType: ResourceTypeExplorer | 'unknown';
  disableMagnifiedNode: boolean;
}

export const LensNodeBadge = <TDbtDagNode extends DbtDagNode>({
  data,
  resourceType,
  className,
  isMagnified,
  disableMagnifiedNode,
  ...divProps
}: LensNodeBadgeProps<TDbtDagNode>) => {
  // TODO: since parent component LensNode calls getLensInfo,
  // pass it down from there instead of recomputing it
  const { lens, lensContext } = useDagContext();

  const displayProps: Partial<LensDisplayInfo> | null = useMemo(() => {
    return getLensInfo(lens, data, lensContext);
  }, [data, lens, lensContext]);

  if (!displayProps) {
    return null;
  }

  return (
    <LensNodeBadgeDisplay
      {...divProps}
      isMagnified={isMagnified}
      disableMagnifiedNode={disableMagnifiedNode}
      className={twMerge('text-normal size text-xs', className)}
      {...displayProps}
    />
  );
};
